// ===================================================================
// Time & Labor — Summaries
// Aggregated labor summaries by employee, project, or category
// with date range, employee, project, and "Unassigned" filters
// ===================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  Title, Text, Paper, Table, SimpleGrid, Group, Select, Button,
  LoadingOverlay, SegmentedControl, TextInput, Badge,
} from '../../components/gab/GabComponents';
import { IconDownload, IconCalendar } from '@tabler/icons-react';
import { useTimeLaborStore } from '../../store/timeLaborStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatCurrency, stripCurrency } from '../../lib/formatUtils';
import { exportToCSV } from '../../lib/exportUtils';

const TEAL = '#0D9488';

/** Extract the task label from activityDescription (e.g. "Debris Removal: cleaned lot" -> "Debris Removal") */
function extractTaskLabel(desc: string): string {
  if (!desc) return '';
  const colonIdx = desc.indexOf(':');
  return colonIdx > 0 ? desc.substring(0, colonIdx).trim() : desc;
}

function pf(v: string | number | undefined): number {
  if (typeof v === 'number') return v || 0;
  return parseFloat(stripCurrency(v || '')) || 0;
}

export default function Summaries() {
  useModulePermission('timelabor');
  const { fetchEmployees, laborRecords, loadingRecords, fetchLaborRecords } = useTimeLaborStore();
  const { projects, fetchProjects } = usePAGrantsStore();
  const activeIncident = useAuthStore(s => s.activeIncident);

  const [groupBy, setGroupBy] = useState<string>('employee');
  const [filterProject, setFilterProject] = useState<string | null>(null);
  const [filterEmployee, setFilterEmployee] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const activeIncidentId = activeIncident?.id ?? null;
  useEffect(() => {
    fetchEmployees();
    fetchLaborRecords();
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIncidentId]);

  const projectLookup = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach(p => m.set(String(p.Rid), p.projectTitle || p.projectNumber || `#${p.Rid}`));
    return m;
  }, [projects]);

  // Project filter options (include "Unassigned")
  const projectFilterOptions = useMemo(() => [
    { value: '__unassigned__', label: 'Unassigned' },
    ...projects.map(p => ({
      value: String(p.Rid),
      label: `${p.projectNumber ? p.projectNumber + ' - ' : ''}${p.projectTitle || 'Untitled'}`,
    })),
  ], [projects]);

  // Employee filter options
  const employeeFilterOptions = useMemo(() => {
    const names = new Set(laborRecords.map(r => r.employeeName).filter(Boolean));
    return [...names].sort().map(n => ({ value: n, label: n }));
  }, [laborRecords]);

  // Filter by active incident + user filters
  const filtered = useMemo(() => {
    if (!activeIncident) return [];
    const incId = String(activeIncident.id);
    let list = laborRecords.filter(r => String(r.incidentId ?? '') === incId);
    if (filterProject === '__unassigned__') {
      list = list.filter(r => !r.projectId);
    } else if (filterProject) {
      list = list.filter(r => String(r.projectId) === filterProject);
    }
    if (filterEmployee) list = list.filter(r => r.employeeName === filterEmployee);
    if (dateFrom) list = list.filter(r => r.workDate >= dateFrom);
    if (dateTo) list = list.filter(r => r.workDate <= dateTo);
    return list;
  }, [laborRecords, activeIncident, filterProject, filterEmployee, dateFrom, dateTo]);

  // Summarize
  type SummaryRow = { key: string; label: string; category: string; regHrs: number; otHrs: number; laborCost: number; fringeCost: number; totalCost: number; entryCount: number };
  const summaryRows = useMemo<SummaryRow[]>(() => {
    const map = new Map<string, SummaryRow>();
    filtered.forEach(r => {
      const taskLabel = extractTaskLabel(r.activityDescription) || 'Uncategorized';
      const projectKey = r.projectId ? String(r.projectId) : '__unassigned__';
      const key = groupBy === 'employee' ? r.employeeName
        : groupBy === 'project' ? projectKey
        : taskLabel;
      const label = groupBy === 'employee' ? r.employeeName
        : groupBy === 'project' ? (r.projectId ? (projectLookup.get(String(r.projectId)) || String(r.projectId)) : 'Unassigned')
        : taskLabel;
      if (!map.has(key)) map.set(key, { key, label, category: r.category || '', regHrs: 0, otHrs: 0, laborCost: 0, fringeCost: 0, totalCost: 0, entryCount: 0 });
      const row = map.get(key)!;
      row.regHrs += pf(r.hoursRegular);
      row.otHrs += pf(r.hoursOvertime);
      row.laborCost += pf(r.laborCost);
      row.fringeCost += pf(r.fringeCost);
      row.totalCost += pf(r.totalCost);
      row.entryCount++;
    });
    return [...map.values()].sort((a, b) => b.totalCost - a.totalCost);
  }, [filtered, groupBy, projectLookup]);

  const grandTotal = useMemo(() =>
    summaryRows.reduce((acc, r) => ({
      regHrs: acc.regHrs + r.regHrs,
      otHrs: acc.otHrs + r.otHrs,
      laborCost: acc.laborCost + r.laborCost,
      fringeCost: acc.fringeCost + r.fringeCost,
      totalCost: acc.totalCost + r.totalCost,
      entries: acc.entries + r.entryCount,
    }), { regHrs: 0, otHrs: 0, laborCost: 0, fringeCost: 0, totalCost: 0, entries: 0 }),
    [summaryRows]
  );

  const handleExport = () => {
    exportToCSV(`labor-summary-by-${groupBy}`, [
      groupBy === 'employee' ? 'Employee' : groupBy === 'project' ? 'Project' : 'Category',
      'Entries', 'Regular Hrs', 'OT Hrs', 'Labor Cost', 'Fringe Cost', 'Total Cost',
    ], summaryRows.map(r => [
      r.label, String(r.entryCount), r.regHrs.toFixed(1), r.otHrs.toFixed(1),
      r.laborCost.toFixed(2), r.fringeCost.toFixed(2), r.totalCost.toFixed(2),
    ]));
  };

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={loadingRecords && laborRecords.length === 0} />

      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>Labor Summaries</Title>
          <Text size="sm" c="dimmed">Aggregated force account labor by employee, project, or category</Text>
        </div>
        <Button variant="light" color="#939598" leftSection={<IconDownload size={14} />} onClick={handleExport}>
          Export CSV
        </Button>
      </Group>

      {/* Stat cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mb="lg">
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${TEAL}` }}>
          <Text size="xl" fw={700}>{grandTotal.entries}</Text>
          <Text size="xs" c="dimmed">Total Entries</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #2563EB' }}>
          <Text size="xl" fw={700}>{(grandTotal.regHrs + grandTotal.otHrs).toFixed(1)}</Text>
          <Text size="xs" c="dimmed">Total Hours</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
          <Text size="xl" fw={700}>{formatCurrency(grandTotal.laborCost.toFixed(2))}</Text>
          <Text size="xs" c="dimmed">Labor Cost</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #7C3AED' }}>
          <Text size="xl" fw={700}>{formatCurrency(grandTotal.totalCost.toFixed(2))}</Text>
          <Text size="xs" c="dimmed">Total (Labor + Fringe)</Text>
        </Paper>
      </SimpleGrid>

      {/* Controls */}
      <Group gap={8} mb="md">
        <SegmentedControl size="xs" value={groupBy} onChange={setGroupBy} data={[
          { value: 'employee', label: 'By Employee' },
          { value: 'project', label: 'By Project' },
          { value: 'category', label: 'By Category' },
        ]} />
        <TextInput size="xs" type="date" placeholder="From" value={dateFrom}
          onChange={e => setDateFrom(e.target.value)} style={{ width: 140 }}
          leftSection={<IconCalendar size={14} />} />
        <TextInput size="xs" type="date" placeholder="To" value={dateTo}
          onChange={e => setDateTo(e.target.value)} style={{ width: 140 }} />
        <Select size="xs" placeholder="All Employees" data={employeeFilterOptions} value={filterEmployee}
          onChange={setFilterEmployee} clearable searchable style={{ width: 180 }} />
        <Select size="xs" placeholder="All Projects" data={projectFilterOptions} value={filterProject}
          onChange={setFilterProject} clearable searchable style={{ width: 220 }} />
        <Text size="xs" c="dimmed">{summaryRows.length} group(s)</Text>
      </Group>

      {/* Summary table */}
      <Paper withBorder>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 380px)' }}>
        <Table striped highlightOnHover>
          <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
            <Table.Tr>
              <Table.Th>{groupBy === 'employee' ? 'Employee' : groupBy === 'project' ? 'Project' : 'Category'}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Entries</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Reg Hrs</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>OT Hrs</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Total Hrs</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Labor Cost</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Fringe Cost</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Total Cost</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {summaryRows.length === 0 ? (
              <Table.Tr><Table.Td colSpan={8}><Text size="sm" c="dimmed" ta="center" py="lg">No data.</Text></Table.Td></Table.Tr>
            ) : summaryRows.map(r => (
              <Table.Tr key={r.key}>
                <Table.Td fw={500}>
                  <Group gap={4} wrap="nowrap">
                    {r.label}
                    {groupBy === 'category' && r.category && (
                      <Badge size="xs" variant="light" color="#939598">{r.category}</Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{r.entryCount}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{r.regHrs.toFixed(1)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{r.otHrs.toFixed(1)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }} fw={600}>{(r.regHrs + r.otHrs).toFixed(1)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(r.laborCost.toFixed(2))}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(r.fringeCost.toFixed(2))}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }} fw={700}>{formatCurrency(r.totalCost.toFixed(2))}</Table.Td>
              </Table.Tr>
            ))}
            {summaryRows.length > 1 && (
              <Table.Tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
                <Table.Td>GRAND TOTAL</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{grandTotal.entries}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{grandTotal.regHrs.toFixed(1)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{grandTotal.otHrs.toFixed(1)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{(grandTotal.regHrs + grandTotal.otHrs).toFixed(1)}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(grandTotal.laborCost.toFixed(2))}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(grandTotal.fringeCost.toFixed(2))}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(grandTotal.totalCost.toFixed(2))}</Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        </div>
      </Paper>
    </div>
  );
}
